import { createContext, useContext, useEffect, useRef } from "react";
import { ConfigurationItemKind, useConfigurationNavigationApi, useConfigurationNavigationItems, useCsAttribute, useCsCfgObject, } from "@plansoft-configuration-renderer/framework";
import { useCurrentLayoutCell } from "@plansoft-configuration-renderer/layout";
import { scrollToElementIfNotInViewPort } from "@plansoft-configuration-renderer/utils";
import { composeGroupPath } from "./composeGroupPath";
/**
 * Can be used to provide a custom scroll function to the configuration scrolling hooks.
 */
export const ConfigurationScrollFunctionContext = createContext(function scrollWithOffset(element) {
    scrollToElementIfNotInViewPort(element, -100);
});
ConfigurationScrollFunctionContext.displayName =
    "ConfigurationScrollFunctionContext";
function useConfigurationScrolling(options) {
    const elementRef = useRef(null);
    const items = useConfigurationNavigationItems();
    const { setItems } = useConfigurationNavigationApi();
    const scrollFromContext = useContext(ConfigurationScrollFunctionContext);
    const { path, kind, cellId, scrollFunction = scrollFromContext } = options;
    useEffect(() => {
        const lastItem = items[items.length - 1];
        if (lastItem == null) {
            return;
        }
        if (lastItem.path !== path || lastItem.kind !== kind) {
            return;
        }
        if (lastItem.cellId !== cellId) {
            return;
        }
        const domItem = elementRef === null || elementRef === void 0 ? void 0 : elementRef.current;
        if (domItem == null) {
            return;
        }
        // scroll to the element
        scrollFunction(domItem);
        // clear the items, so we do not accidentally scroll or expand again
        setItems([]);
    }, [setItems, scrollFunction, items, path, kind, cellId]);
    return elementRef;
}
export function useConfigurationObjectScrolling(options) {
    const { configurationObject, scrollFunction } = options;
    const { path } = configurationObject;
    return useConfigurationScrolling({
        kind: ConfigurationItemKind.ConfigurationObject,
        path,
        cellId: undefined, // does not have a cell currently
        scrollFunction,
    });
}
export function useConfigurationGroupScrolling(options) {
    const { groupId, scrollFunction } = options;
    const { path } = useCsCfgObject();
    const cell = useCurrentLayoutCell({ visibleLayoutOnly: true });
    return useConfigurationScrolling({
        kind: ConfigurationItemKind.ConfigurationGroup,
        path: composeGroupPath({ groupId, configurationObjectPath: path }),
        cellId: cell === null || cell === void 0 ? void 0 : cell.id,
        scrollFunction,
    });
}
export function useConfigurationAttributeScrolling(options = {}) {
    const { scrollFunction } = options;
    const attribute = useCsAttribute();
    const cell = useCurrentLayoutCell({ visibleLayoutOnly: true });
    return useConfigurationScrolling({
        kind: ConfigurationItemKind.ConfigurationAttribute,
        path: attribute.path,
        cellId: cell === null || cell === void 0 ? void 0 : cell.id,
        scrollFunction,
    });
}
//# sourceMappingURL=useConfigurationScrolling.js.map